import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "@/hooks/useToast"
import { useGenesysTelephony } from "@/modules/telephony/hooks/useGenesysTelephony"
import { getDistinctPhoneNumbers } from "@/modules/telephony/lib/get-distinct-phone-numbers"
import { useConsultQueueMutation } from "@/modules/telephony/services/useConsultQueueMutation"
import { ConsultQueueKind, Country, Language } from "@/types/__generated__/union-types"

type ConsultQueueDescriptor = {
  queue: ConsultQueueKind
  translationKey: string
  testId: string
}

const CONSULT_QUEUES: ReadonlyArray<ConsultQueueDescriptor> = [
  {
    queue: "MEDICAL_SUPERVISOR",
    translationKey: "telephony.consultActions.medicalSupervisor",
    testId: "consult-medical-supervisor-button",
  },
  {
    queue: "OPERATIONS_LEAD",
    translationKey: "telephony.consultActions.operationsLead",
    testId: "consult-operations-lead-button",
  },
  { queue: "DOCTOR", translationKey: "telephony.consultActions.doctor", testId: "consult-doctor-button" },
  {
    queue: "PEDIATRIC",
    translationKey: "telephony.consultActions.pediatrician",
    testId: "consult-pediatrician-button",
  },
]

const useConsultActions = (
  country: Country,
  patientLanguage: Language,
  patientId: string | undefined,
  phoneNumbers: ReadonlyArray<string | null | undefined>,
) => {
  const { t } = useTranslation()
  const { isGenesysCallEnabled, isGenesysLoginInFlight, openGenesysLoginWindow, runViaGenesys } = useGenesysTelephony()
  const [consultQueueMutation, isConsultQueueMutationInFlight] = useConsultQueueMutation()
  const [queueBeingConsulted, setQueueBeingConsulted] = useState<ConsultQueueKind | null>(null)

  const distinctPhoneNumbers = getDistinctPhoneNumbers(phoneNumbers)

  const showConsultStartedToast = (descriptor: ConsultQueueDescriptor) =>
    toast({
      description: t("telephony.consultActions.callTransferred", { destination: t(descriptor.translationKey) }),
    })

  const sendConsultQueue = (descriptor: ConsultQueueDescriptor) => {
    setQueueBeingConsulted(descriptor.queue)
    consultQueueMutation(
      {
        queue: descriptor.queue,
        language: patientLanguage,
        patientId,
        phoneNumbers: distinctPhoneNumbers,
        country,
      },
      () => showConsultStartedToast(descriptor),
      () => openGenesysLoginWindow(() => sendConsultQueue(descriptor)),
    )
  }

  const consultQueue = (descriptor: ConsultQueueDescriptor) => runViaGenesys(() => sendConsultQueue(descriptor))

  return {
    isGenesysCallEnabled,
    consultQueue,
    isConsultInFlight: isGenesysLoginInFlight || isConsultQueueMutationInFlight,
    queueBeingConsulted,
  }
}

export { CONSULT_QUEUES, useConsultActions, type ConsultQueueDescriptor }
